import qs from "qs";
import GlobalContainer from "~/components/global_container";
import { signOutAndRedirect as redirectToSignIn } from "~/lib/authSession";
import {
  isTokenExpiringSoon,
  refreshToken,
  stopBackgroundRefresh,
} from "~/lib/tokenManager";
import { getToken } from "~/lib/tokenStore";

const FORBIDDEN_MESSAGE = "You do not have permission to perform that action.";

const signOutAndRedirect = () => {
  redirectToSignIn({ stopRefresh: stopBackgroundRefresh });
};

const applyAuth = (init) => ({
  ...init,
  headers: {
    Authorization: `Bearer ${getToken()}`,
    ...init.headers,
  },
});

// Core authenticated fetch with 401 retry and 403 notification.
// For streaming/file responses use this directly — it returns raw Response.
const authFetch = async (input, options = {}) => {
  const { isRequestCurrent, handledForbiddenCodes = [], ...init } = options;
  const assertCurrent = () => {
    if (isRequestCurrent && (init.signal?.aborted || !isRequestCurrent())) {
      throw new DOMException("Request scope ended", "AbortError");
    }
  };
  assertCurrent();
  const response = await fetch(input, applyAuth(init));
  assertCurrent();

  if (response.status === 401) {
    try {
      assertCurrent();
      await refreshToken();
    } catch (error) {
      assertCurrent();
      signOutAndRedirect();
      return response;
    }
    assertCurrent();
    const retried = await fetch(input, applyAuth(init));
    assertCurrent();
    return retried;
  }

  if (response.status === 403) {
    let handled = false;
    if (handledForbiddenCodes.length) {
      try {
        const payload = await response.clone().json();
        handled = handledForbiddenCodes.includes(
          payload?.code || payload?.error,
        );
      } catch {
        // Unrecognized responses still receive the default permission notification.
      }
    }
    assertCurrent();
    if (!handled) {
      GlobalContainer.notify(FORBIDDEN_MESSAGE, "error");
    }
  }

  return response;
};

// Parse JSON response, throw on API errors
const parseJSON = async (response) => {
  const text = (await response.text()).trim();
  const body = text ? JSON.parse(text) : null;

  if (body?.errors) {
    throw { ...body.errors, ...body };
  }

  if (!response.ok) {
    throw { status: response.status, ...(body != null ? body : {}) };
  }

  return body;
};

// --- XHR upload with progress ---
// Returns { promise, abort } for file uploads that need progress tracking.
// Works with any URL (internal API or external like S3).
//
// Usage:
//   const { promise, abort } = uploadWithProgress(url, { body: formData, onProgress });
//   const data = await promise;

const uploadWithProgress = (
  url,
  { method = "POST", body, headers = {}, onProgress } = {},
) => {
  const xhr = new XMLHttpRequest();
  xhr.open(method, url);

  Object.entries(headers).forEach(([key, value]) => {
    if (value != null) {
      xhr.setRequestHeader(key, String(value));
    }
  });

  if (onProgress) {
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress((e.loaded / e.total) * 100);
      }
    };
  }

  const promise = new Promise((resolve, reject) => {
    xhr.onload = () => {
      let parsed;
      try {
        parsed = JSON.parse(xhr.responseText);
      } catch {
        parsed = xhr.responseText;
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parsed);
      } else {
        reject(parsed);
      }
    };

    xhr.onerror = () => reject(new Error("Network error"));
    xhr.onabort = () =>
      reject(new DOMException("Upload aborted", "AbortError"));
    xhr.send(body);
  });

  return { promise, abort: () => xhr.abort() };
};

// --- Client factory ---
// Creates a fetch client bound to a base URL with convenience methods.
//
// Usage:
//   const data = await apiFetch.get('/leads', { params: { page: 1 } });
//   const data = await apiFetch.post('/leads', { json: { name: 'John' } });
//   const raw  = await apiFetch.raw('/leads');  // raw Response (streaming)
//
// Options:
//   json: {}   — auto Content-Type + JSON.stringify + defaultData
//   params: {} — auto qs.stringify with arrayFormat: brackets + defaultData
//   body, headers, signal, etc. — passed through to fetch

const createClient = (
  baseUrl,
  {
    baseFetch = authFetch,
    defaultHeaders = () => ({}),
    defaultData = () => ({}),
    authHeaders = () => ({}),
    prefix = "",
  } = {},
) => {
  const buildUrl = (path, params, overridePrefix) => {
    const pfx = overridePrefix !== undefined ? overridePrefix : prefix;
    const url = `${baseUrl}${pfx}${path}`;
    const merged = { ...params, ...defaultData() };
    const keys = Object.keys(merged);
    if (keys.length === 0) {
      return url;
    }

    const query = qs.stringify(merged, {
      arrayFormat: "brackets",
      skipNulls: true,
    });
    if (!query) {
      return url;
    }
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${query}`;
  };

  const buildInit = ({ json, params, ...init }) => {
    const headers = { ...defaultHeaders(), ...init.headers };

    if (json) {
      headers["Content-Type"] = "application/json";
      init.body = JSON.stringify({ ...json, ...defaultData() });
    }

    return { ...init, headers };
  };

  // Raw fetch — returns Response (for streaming, file downloads)
  const raw = (path, options = {}) => {
    const { params, prefix: pfx, ...rest } = options;
    return baseFetch(buildUrl(path, params, pfx), buildInit(rest));
  };

  // JSON fetch — returns abortable promise (parsed body, throws on errors)
  // Use as a normal promise: await client.get("/path")
  // Or abort it:  const req = client.get("/path"); req.abort();
  const request = (method, path, options = {}) => {
    const controller = new AbortController();
    const promise = raw(path, {
      method,
      signal: controller.signal,
      ...options,
    }).then(parseJSON);
    promise.abort = () => controller.abort();
    return promise;
  };

  // XHR upload with progress — returns { promise, abort }
  const upload = (
    path,
    { headers: customHeaders, prefix: pfx, ...rest } = {},
  ) => {
    const headers = { ...authHeaders(), ...defaultHeaders(), ...customHeaders };
    return uploadWithProgress(buildUrl(path, undefined, pfx), {
      ...rest,
      headers,
    });
  };

  return {
    raw,
    upload,
    get: (path, options = {}) => request("GET", path, options),
    post: (path, options = {}) => request("POST", path, options),
    put: (path, options = {}) => request("PUT", path, options),
    patch: (path, options = {}) => request("PATCH", path, options),
    delete: (path, options = {}) => request("DELETE", path, options),
  };
};

// --- Preconfigured clients ---

const teamHeaders = () =>
  Rails?.helpers?.currentTeam ? { team: Rails.helpers.currentTeam.id } : {};

const apiDefaults = {
  defaultHeaders: teamHeaders,
  defaultData: () => ({ product: GlobalContainer?.product() }),
  authHeaders: () => ({ Authorization: `Bearer ${getToken()}` }),
};

const apiFetch = createClient(Rails.apiUrl, {
  ...apiDefaults,
  prefix: "/api/v1",
});
const apiV2Fetch = createClient(Rails.apiUrl, {
  baseFetch: (...args) => fetch(...args),
  defaultData: () => ({ product: GlobalContainer?.product() }),
  prefix: "/v2",
});
const mlsFetch = createClient(Rails.mlsServiceUrl, { prefix: "/v1" });

// Authenticated fetch for streaming endpoints (e.g. AI service).
// Proactively refreshes token before request to avoid mid-stream expiry.
// Throws with server error message on non-ok responses.
const streamFetch = async (input, init = {}) => {
  if (isTokenExpiringSoon(getToken())) {
    await refreshToken().catch(() => {
      // Ignore errors - we'll retry on next request
    });
  }

  const response = await authFetch(input, init);

  if (!response.ok) {
    let message = `Service error (${response.status})`;
    try {
      const json = await response.clone().json();
      if (json?.error) {
        message = json.error;
      }
    } catch {
      // response wasn't JSON — use default message
    }

    if (response.status === 429) {
      const concurrent = response.headers.get("X-Concurrent-Limit");
      message = concurrent
        ? "Too many active generations. Please wait for one to finish."
        : "Too many requests. Please wait a moment and try again.";
    } else if (response.status === 409) {
      message =
        "Duplicate request detected. Please wait a moment before retrying.";
    } else if (response.status === 413) {
      message =
        "Message is too large. Please shorten your input and try again.";
    }

    const err = new Error(message);
    err.status = response.status;
    throw err;
  }

  return response;
};

export default authFetch;
export {
  apiFetch,
  apiV2Fetch,
  mlsFetch,
  streamFetch,
  parseJSON,
  signOutAndRedirect,
  createClient,
  uploadWithProgress,
};
